/**
 * localStorage wrapper for consent choices.
 * Key format: `${namespace}:${siteId}:consent`. Each site is scoped so
 * a visitor on multiple Consenteer-managed sites keeps separate prefs.
 */

import type { ConsentChoices } from "./types.js";

export interface StoredConsent {
  choices: ConsentChoices;
  consentVersion: string;
  storedAt: number;
  expiresAt: number;
}

export class ConsentStorage {
  constructor(
    private readonly namespace: string,
    private readonly siteId: string,
  ) {}

  private get key(): string {
    return `${this.namespace}:${this.siteId}:consent`;
  }

  read(): StoredConsent | null {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredConsent;
      if (typeof parsed.expiresAt !== "number" || parsed.expiresAt < Date.now()) {
        this.clear();
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  write(choices: ConsentChoices, consentVersion: string, ttlDays: number): StoredConsent {
    const now = Date.now();
    const data: StoredConsent = {
      choices,
      consentVersion,
      storedAt: now,
      expiresAt: now + ttlDays * 24 * 60 * 60 * 1000,
    };
    try {
      localStorage.setItem(this.key, JSON.stringify(data));
    } catch {
      /* quota or denied — ignore, server-side log of consent is the source of truth */
    }
    return data;
  }

  clear(): void {
    try {
      localStorage.removeItem(this.key);
    } catch {
      /* ignore */
    }
  }
}
